import { defineStore } from 'pinia';

import type { EmailTemplateVariable } from '@stuut/api/models';
import { waitFor } from '@stuut/common/src/utils';

import { stuut } from '@/api';

interface State {
  loading: boolean;
  variables: EmailTemplateVariable[];
}

export const useEmailTemplateVariablesStore = defineStore('emailTemplateVariables', {
  actions: {
    async fetchVariables(force = false) {
      try {
        if (!force && (this.$state.variables.length || this.$state.loading)) {
          await waitFor(() => !this.$state.loading, 250);
        } else {
          this.$patch({ loading: true });
          const variables = await stuut.emailTemplates.getVariables();
          this.$patch({
            variables: variables ?? [],
            loading: false,
          });
        }

        return this.$state.variables;
      } catch (error: any) {
        console.error(error);
        this.$patch({ loading: false });
        throw error;
      }
    },

    async refetch() {
      return this.fetchVariables(true);
    },
  },

  getters: {
    getByName: (state) => {
      return (name: string) => state.variables.find((v) => v.name === name);
    },

    variableMap: (state) => {
      return new Map(state.variables.map((v) => [v.name, v.display_name]));
    },

    invoiceVariables: (state) => {
      return state.variables.filter(
        (v) => v.name.includes('invoice') && !v.name.includes('summary'),
      );
    },

    invoiceSummaryVariables: (state) => {
      return state.variables.filter(
        (v) =>
          v.name.includes('summary') &&
          !v.name.includes('credit_memo') &&
          !v.name.includes('payment'),
      );
    },

    creditMemoSummaryVariables: (state) => {
      return state.variables.filter(
        (v) => v.name.includes('credit_memo') && v.name.includes('summary'),
      );
    },

    paymentSummaryVariables: (state) => {
      const specificPaymentFields = [
        'payment_amount',
        'payment_date',
        'payment_reference',
        'payment_allocations_summary',
        'payment_allocations_table_summary',
      ];
      return state.variables.filter(
        (v) =>
          v.name.includes('payment') &&
          v.name.includes('summary') &&
          !specificPaymentFields.includes(v.name),
      );
    },

    paymentVariables: (state) => {
      const specificPaymentFields = [
        'payment_amount',
        'payment_date',
        'payment_reference',
        'payment_allocations_summary',
        'payment_allocations_table_summary',
      ];
      return state.variables.filter((v) => specificPaymentFields.includes(v.name));
    },

    bankTransactionVariables: (state) => {
      return state.variables.filter((v) => v.name.includes('transaction'));
    },

    balanceVariables: (state) => {
      return state.variables.filter((v) => v.name.includes('balance'));
    },

    customerVariables: (state) => {
      return state.variables.filter((v) => v.name.includes('customer'));
    },

    senderVariables: (state) => {
      return state.variables.filter((v) => v.name.includes('sender'));
    },

    customVariables: (state) => {
      return state.variables.filter((v) => v.name.startsWith('cf_'));
    },

    customTableVariables: (state) => {
      return state.variables.filter((v) => v.name.startsWith('custom_table_'));
    },

    uncategorizedVariables: (state) => {
      const specificPaymentFields = [
        'payment_amount',
        'payment_date',
        'payment_reference',
        'payment_allocations_summary',
        'payment_allocations_table_summary',
      ];

      return state.variables.filter((v) => {
        // Exclude if matches any category
        if (v.name.startsWith('custom_table_')) return false;
        if (v.name.startsWith('cf_')) return false;
        if (v.name.includes('customer')) return false;
        if (v.name.includes('credit_memo') && v.name.includes('summary')) return false;
        if (specificPaymentFields.includes(v.name)) return false;
        if (v.name.includes('payment') && v.name.includes('summary')) return false;
        if (v.name.includes('summary')) return false;
        if (v.name.includes('invoice')) return false;
        if (v.name.includes('balance')) return false;
        if (v.name.includes('sender')) return false;
        if (v.name.includes('transaction')) return false;

        return true;
      });
    },

    variableCategories(): Array<{ name: string; variables: EmailTemplateVariable[] }> {
      return [
        { name: 'Invoice', variables: this.invoiceVariables },
        { name: 'Invoice Summary', variables: this.invoiceSummaryVariables },
        { name: 'Credit Memo Summary', variables: this.creditMemoSummaryVariables },
        { name: 'Unallocated Payments', variables: this.paymentSummaryVariables },
        { name: 'Payment', variables: this.paymentVariables },
        { name: 'Bank Transaction', variables: this.bankTransactionVariables },
        { name: 'Balance', variables: this.balanceVariables },
        { name: 'Customer', variables: this.customerVariables },
        { name: 'Sender', variables: this.senderVariables },
        { name: 'Custom', variables: this.customVariables },
        { name: 'Custom Tables', variables: this.customTableVariables },
      ];
    },
  },

  state: (): State => ({
    loading: false,
    variables: [],
  }),
});

export default useEmailTemplateVariablesStore;
